import { useCallback } from 'react'
import type { AuthAppRole, AuthMePlayer, AuthOrganization } from '@/app/providers/auth-context'
import { normalizeOrgRole } from '@/app/providers/auth-profile'

type AuthPermissionsInput = {
  me: AuthMePlayer | null
  organizations: AuthOrganization[]
  currentOrgId: string | null
}

type AuthPermissions = {
  isSuperAdmin: () => boolean
  isClubStaff: (orgId?: string) => boolean
  isAnyClubStaff: () => boolean
  canManageFollowers: (orgId?: string) => boolean
  canEditStaff: (orgId?: string) => boolean
  canManageClub: (orgId?: string) => boolean
}

/**
 * The "what may this session do" half of AuthProvider: every permission the
 * context exposes, derived from the resolved player + club list. Lives apart
 * from the session lifecycle (tokens, refresh, login) so the two can be read
 * independently.
 */
export function useAuthPermissions({ me, organizations, currentOrgId }: AuthPermissionsInput): AuthPermissions {
  const getOrgRole = useCallback(
    (orgId?: string): AuthAppRole | null => {
      const requestedOrgId = String(orgId || currentOrgId || '').trim()
      if (!requestedOrgId) return null
      const organization = organizations.find((row) => row.orgId === requestedOrgId)
      if (!organization) return null
      return normalizeOrgRole(organization.role)
    },
    [currentOrgId, organizations],
  )

  const isSuperAdmin = useCallback((): boolean => {
    const platformRole = String(me?.platform_role || '').trim().toLowerCase()
    const result = platformRole === 'super_admin'
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[isSuperAdmin]', { platformRole, meIsNull: me == null, result })
    }
    return result
  }, [me])

  const isClubStaff = useCallback(
    (orgId?: string): boolean => {
      if (isSuperAdmin()) return true
      const role = getOrgRole(orgId)
      return role === 'CLUB_ADMIN' || role === 'COACH'
    },
    [getOrgRole, isSuperAdmin],
  )

  const isAnyClubStaff = useCallback((): boolean => {
    const superAdmin = isSuperAdmin()
    if (superAdmin) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[isAnyClubStaff]', {
          superAdmin: true,
          orgsCount: organizations.length,
          staffOrgsCount: 0,
          result: true,
        })
      }
      return true
    }
    const staffOrgs = organizations.filter((org) => {
      const role = normalizeOrgRole(org.role)
      return role === 'CLUB_ADMIN' || role === 'COACH'
    })
    const result = staffOrgs.length > 0
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[isAnyClubStaff]', {
        superAdmin: false,
        orgsCount: organizations.length,
        staffOrgsCount: staffOrgs.length,
        platformRole: me?.platform_role || null,
        result,
      })
    }
    return result
  }, [isSuperAdmin, me, organizations])

  const canManageFollowers = useCallback(
    (orgId?: string): boolean => {
      if (isSuperAdmin()) return true
      const requestedOrgId = String(orgId || currentOrgId || '').trim()
      const organization = organizations.find((row) => row.orgId === requestedOrgId)
      const sourceRole = String(organization?.role || '').trim().toLowerCase()
      return sourceRole === 'owner' || sourceRole === 'admin'
    },
    [currentOrgId, isSuperAdmin, organizations],
  )

  // THE STAFF EDITOR IS THE OWNER'S (/ux staff-access 1b; the server's role change and removal are
  // owner-only, docs/20_rbac.md). Deliberately NOT `canManageFollowers`: that one is the
  // owner/admin finance and settings gate (Payments, refunds, coach tiers, API keys, Activity).
  const canEditStaff = useCallback(
    (orgId?: string): boolean => {
      if (isSuperAdmin()) return true
      const requestedOrgId = String(orgId || currentOrgId || '').trim()
      const organization = organizations.find((row) => row.orgId === requestedOrgId)
      return String(organization?.role || '').trim().toLowerCase() === 'owner'
    },
    [currentOrgId, isSuperAdmin, organizations],
  )

  const canManageClub = useCallback(
    (orgId?: string): boolean => {
      const superAdmin = isSuperAdmin()
      if (superAdmin) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log('[canManageClub]', { superAdmin: true, orgId, role: null, result: true })
        }
        return true
      }
      const role = getOrgRole(orgId)
      const result = role === 'CLUB_ADMIN' || role === 'COACH'
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[canManageClub]', { superAdmin: false, orgId, role, result })
      }
      return result
    },
    [getOrgRole, isSuperAdmin],
  )

  return { isSuperAdmin, isClubStaff, isAnyClubStaff, canManageFollowers, canEditStaff, canManageClub }
}
